/**
 * ChallengeSubmitModal — Submit a score for the weekly challenge.
 * Shows recent lifts that match the challenge, or a manual entry field.
 */
import { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  FlatList,
  Platform,
} from 'react-native';
import { X, Check, Trophy } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { supabase } from '@/lib/supabase';
import { submitChallengeScore, getUserSubmission, type WeeklyChallenge } from '@/lib/weeklyChallenges';

interface Props {
  visible: boolean;
  challenge: WeeklyChallenge;
  onClose: () => void;
  onSubmitted: () => void;
}

interface RecentLift {
  id: string;
  canonical_name: string;
  lift_key: string;
  weight: number;
  reps: number;
  performed_at: string;
}

export default function ChallengeSubmitModal({ visible, challenge, onClose, onSubmitted }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { session } = useAuth();
  const [recentLifts, setRecentLifts] = useState<RecentLift[]>([]);
  const [manualScore, setManualScore] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingScore, setExistingScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (visible && session?.user?.id) {
      loadData();
    }
  }, [visible, session?.user?.id]);

  const loadData = async () => {
    setLoading(true);
    const userId = session!.user.id;

    // Check existing submission
    const existing = await getUserSubmission(challenge.id, userId);
    if (existing) {
      setExistingScore(existing.score);
      setSubmitted(true);
    }

    // Fetch recent lifts matching the challenge
    if (challenge.target_lift) {
      const { data } = await supabase
        .from('strength_events')
        .select('id, canonical_name, lift_key, weight, reps, performed_at')
        .eq('profile_id', userId)
        .or(`lift_key.eq.${challenge.target_lift},canonical_name.eq.${challenge.target_lift}`)
        .order('performed_at', { ascending: false })
        .limit(10);
      setRecentLifts((data ?? []) as RecentLift[]);
    }

    setLoading(false);
  };

  const handleSubmitFromLift = async (lift: RecentLift) => {
    if (!session?.user?.id) return;
    setSubmitting(true);

    let score = lift.weight;
    if (challenge.target_metric === 'reps') score = lift.reps;
    if (challenge.target_metric === 'volume') score = lift.weight * lift.reps;

    const { success } = await submitChallengeScore(
      challenge.id,
      session.user.id,
      score,
      undefined,
      lift.id,
    );

    if (success) {
      setSubmitted(true);
      setExistingScore(score);
      onSubmitted();
    }
    setSubmitting(false);
  };

  const handleManualSubmit = async () => {
    if (!session?.user?.id || !manualScore) return;
    setSubmitting(true);

    const score = parseFloat(manualScore);
    if (isNaN(score) || score <= 0) {
      setSubmitting(false);
      return;
    }

    const { success } = await submitChallengeScore(
      challenge.id,
      session.user.id,
      score,
    );

    if (success) {
      setSubmitted(true);
      setExistingScore(score);
      onSubmitted();
    }
    setSubmitting(false);
  };

  const metricLabel = challenge.target_metric === 'reps' ? 'reps'
    : challenge.target_metric === 'time' ? 'seconds'
    : challenge.target_metric === 'volume' ? 'lbs'
    : 'lbs';

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.sheet, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
              {challenge.title}
            </Text>
            <TouchableOpacity onPress={onClose}>
              <X size={24} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          <Text style={[styles.description, { color: c.textSecondary }]}>
            {challenge.description}
          </Text>

          {loading ? (
            <ActivityIndicator color={c.accent} style={{ marginVertical: 24 }} />
          ) : submitted ? (
            <View style={styles.submittedContainer}>
              <Check size={32} color={c.accent} />
              <Text style={[styles.submittedText, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
                SCORE SUBMITTED
              </Text>
              <Text style={[styles.scoreValue, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {existingScore} {metricLabel}
              </Text>
            </View>
          ) : (
            <>
              {/* Recent matching lifts */}
              {recentLifts.length > 0 && (
                <View style={styles.section}>
                  <Text style={[styles.sectionLabel, { color: c.textSecondary, fontFamily: TYPO.body.semibold }]}>
                    RECENT LIFTS
                  </Text>
                  {recentLifts.slice(0, 5).map(lift => (
                    <TouchableOpacity
                      key={lift.id}
                      style={[styles.liftRow, { borderColor: c.border }]}
                      onPress={() => handleSubmitFromLift(lift)}
                      disabled={submitting}
                    >
                      <View>
                        <Text style={[styles.liftWeight, { color: c.text }]}>
                          {lift.weight} lbs × {lift.reps} reps
                        </Text>
                        <Text style={[styles.liftDate, { color: c.textTertiary }]}>
                          {new Date(lift.performed_at).toLocaleDateString()}
                        </Text>
                      </View>
                      <Text style={[styles.submitLabel, { color: c.accent }]}>Submit →</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {/* Manual entry */}
              <View style={styles.section}>
                <Text style={[styles.sectionLabel, { color: c.textSecondary, fontFamily: TYPO.body.semibold }]}>
                  MANUAL ENTRY
                </Text>
                <View style={styles.manualRow}>
                  <TextInput
                    style={[styles.manualInput, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                    value={manualScore}
                    onChangeText={setManualScore}
                    placeholder={`Enter ${metricLabel}`}
                    placeholderTextColor={c.textTertiary}
                    keyboardType="numeric"
                  />
                  <TouchableOpacity
                    style={[styles.submitBtn, { backgroundColor: c.accent }]}
                    onPress={handleManualSubmit}
                    disabled={submitting || !manualScore}
                  >
                    {submitting ? (
                      <ActivityIndicator size="small" color="#000" />
                    ) : (
                      <Text style={[styles.submitBtnText, { fontFamily: TYPO.heading.bold }]}>
                        SUBMIT
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}

          {/* Close */}
          <TouchableOpacity
            style={[styles.closeBtn, { borderColor: c.border }]}
            onPress={onClose}
          >
            <Text style={[styles.closeBtnText, { color: c.textSecondary }]}>Close</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  sheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    borderBottomWidth: 0,
    padding: 20,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    flex: 1,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 20,
  },
  submittedContainer: {
    alignItems: 'center',
    paddingVertical: 24,
    gap: 8,
  },
  submittedText: {
    fontSize: 16,
    letterSpacing: 1,
  },
  scoreValue: {
    fontSize: 32,
  },
  section: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: 1.5,
    marginBottom: 8,
  },
  liftRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderRadius: 4,
    marginBottom: 6,
  },
  liftWeight: {
    fontSize: 15,
    fontWeight: '600',
  },
  liftDate: {
    fontSize: 11,
    marginTop: 2,
  },
  submitLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  manualRow: {
    flexDirection: 'row',
    gap: 10,
  },
  manualInput: {
    flex: 1,
    height: 48,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 12,
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  submitBtn: {
    paddingHorizontal: 20,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  submitBtnText: {
    fontSize: 14,
    color: '#000',
    letterSpacing: 1,
  },
  closeBtn: {
    paddingVertical: 14,
    alignItems: 'center',
    borderTopWidth: 1,
    marginTop: 12,
  },
  closeBtnText: {
    fontSize: 14,
  },
});
